<script setup lang="ts">
import { computed } from 'vue'

import type {
  InsightConfig,
  InsightConfigJsonField,
  InsightConfigMeta,
} from '../../../models/insightConfig.types'
import { INSIGHT_CONFIG_JSON_FIELD_SPECS } from '../../../service/insightConfig'

import { useI18n } from '@/shared/i18n'
import { RichTextEditor, type RichTextToolbarLabels } from '@/shared/ui/editor'
import { Input } from '@/shared/ui/input'
import { JsonField } from '@/shared/ui/json-field'

const form = defineModel<InsightConfig>('form', { required: true })
const json = defineModel<Record<InsightConfigJsonField, string>>('json', { required: true })
const props = defineProps<{
  meta: InsightConfigMeta
  errors: Record<string, string[]>
  disabled?: boolean
}>()
const { t } = useI18n()
const toolbarLabels = computed<RichTextToolbarLabels>(() => ({
  bold: t('insights.sysadmin.config.editor.bold'),
  italic: t('insights.sysadmin.config.editor.italic'),
  strike: t('insights.sysadmin.config.editor.strike'),
  heading1: t('insights.sysadmin.config.editor.heading1'),
  heading2: t('insights.sysadmin.config.editor.heading2'),
  bulletList: t('insights.sysadmin.config.editor.bulletList'),
  orderedList: t('insights.sysadmin.config.editor.orderedList'),
  blockquote: t('insights.sysadmin.config.editor.blockquote'),
  horizontalRule: t('insights.sysadmin.config.editor.horizontalRule'),
}))

function errorFor(field: string): string {
  return (props.errors[field] ?? [])
    .map((message) => (message.startsWith('insights.') ? t(message) : message))
    .join(' ')
}
</script>

<template>
  <fieldset class="config-section grid gap-md min-w-0 p-lg border border-border rounded-lg">
    <legend>{{ t('insights.sysadmin.config.section.content') }}</legend>
    <Input
      :model-value="form.title_template"
      :label="t('insights.sysadmin.config.fields.titleTemplate')"
      :disabled="disabled"
      :error="Boolean(errorFor('title_template'))"
      :error-message="errorFor('title_template')"
      @update:model-value="form = { ...form, title_template: String($event) }"
    />
    <RichTextEditor
      :model-value="form.content_template"
      :label="t('insights.sysadmin.config.fields.contentTemplate')"
      :toolbar-labels="toolbarLabels"
      :disabled="disabled"
      @update:model-value="form = { ...form, content_template: $event }"
    />
    <JsonField
      :model-value="json.template_variables"
      :spec="INSIGHT_CONFIG_JSON_FIELD_SPECS.template_variables"
      :label="t('insights.sysadmin.config.fields.templateVariables')"
      :description="t('insights.sysadmin.config.jsonDescription.templateVariables')"
      :error-message="errorFor('template_variables')"
      :default-label="t('insights.sysadmin.config.useDefault')"
      :disabled="disabled"
      @update:model-value="json = { ...json, template_variables: $event }"
    />
  </fieldset>
</template>

<style scoped>
.config-section legend {
  padding-inline: var(--ui-space-xs);
  font-weight: 600;
}
</style>
